import React, { useState } from "react";
import { BRAND_LOGOS } from "../data/mock";

/**
 * Floating brand bubbles.
 * - Minimal continuous idle motion (subtle drift)
 * - Hover reveals an oval, elongated brand card with client details
 */
const BUBBLES = [
  {
    key: "aldi",
    logo: BRAND_LOGOS.aldiSued,
    name: "Aldi Süd",
    tag: "Retail",
    metric: "Reach-Kampagnen",
    pos: "top-[-24px] left-[-32px]",
    delay: 0,
    size: "w-[76px] h-[76px]",
    cardSide: "right",
  },
  {
    key: "subway",
    logo: BRAND_LOGOS.subway,
    name: "Subway",
    tag: "QSR",
    metric: "730K Reach · Reel",
    pos: "top-[12%] right-[-38px]",
    delay: 1.2,
    size: "w-[66px] h-[66px]",
    dark: true,
    cardSide: "left",
  },
  {
    key: "fiveguys",
    logo: BRAND_LOGOS.fiveGuys,
    name: "Five Guys",
    tag: "QSR",
    metric: "UGC-Kampagne",
    pos: "top-[38%] left-[-46px]",
    delay: 2.4,
    size: "w-[70px] h-[70px]",
    cardSide: "right",
  },
  {
    key: "amazon",
    logo: BRAND_LOGOS.amazon,
    name: "Amazon",
    tag: "E-Commerce",
    metric: "Brand-Content",
    pos: "bottom-[26%] right-[-32px]",
    delay: 0.6,
    size: "w-[68px] h-[68px]",
    dark: true,
    cardSide: "left",
  },
  {
    key: "loco",
    logo: BRAND_LOGOS.locoChicken,
    name: "Loco Chicken",
    tag: "Gastronomie",
    metric: "Performance-Spot",
    pos: "bottom-[-22px] left-[8%]",
    delay: 3,
    size: "w-[74px] h-[74px]",
    dark: true,
    cardSide: "right",
  },
  {
    key: "prime",
    logo: BRAND_LOGOS.prime,
    name: "Prime Nightlife",
    tag: "Events",
    metric: "Event-Coverage",
    pos: "bottom-[-14px] right-[12%]",
    delay: 1.8,
    size: "w-[62px] h-[62px]",
    dark: true,
    cardSide: "left",
  },
];

const BrandCard = ({ b, side }) => (
  <div
    className={`pointer-events-none absolute top-1/2 -translate-y-1/2 z-30 ${
      side === "right" ? "left-[calc(100%+12px)]" : "right-[calc(100%+12px)]"
    }`}
  >
    <div
      className="rounded-full bg-neutral-950 text-white pl-3 pr-5 py-2.5 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.55)] ring-1 ring-white/10 backdrop-blur-xl flex items-center gap-3 whitespace-nowrap animate-brand-card-in"
      style={{ minWidth: "220px" }}
    >
      <div className="shrink-0 w-9 h-9 rounded-full bg-white flex items-center justify-center overflow-hidden">
        <img
          src={b.logo.src}
          alt={b.name}
          className="max-w-[75%] max-h-[75%] object-contain"
          loading="lazy"
        />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-pink-300">
          {b.tag}
        </div>
        <div className="font-display font-extrabold text-sm leading-tight tracking-tight">
          {b.name}
        </div>
        <div className="text-[11px] text-neutral-400 mt-0.5">{b.metric}</div>
      </div>
    </div>
  </div>
);

const BrandBubbles = () => {
  const [hovered, setHovered] = useState(null);
  return (
    <div className="pointer-events-none absolute inset-0 z-20">
      {BUBBLES.map((b) => (
        <div
          key={b.key}
          onMouseEnter={() => setHovered(b.key)}
          onMouseLeave={() => setHovered((h) => (h === b.key ? null : h))}
          className={`absolute ${b.pos} ${b.size} rounded-full shadow-[0_18px_42px_-12px_rgba(0,0,0,0.35)] flex items-center justify-center backdrop-blur-md border ring-1 ring-black/5 pointer-events-auto cursor-pointer transition-transform duration-500 ${
            hovered === b.key ? "scale-[1.08]" : "animate-brand-drift"
          } ${
            b.dark
              ? "bg-neutral-900/95 border-white/10"
              : "bg-white/95 border-black/5"
          }`}
          style={{ animationDelay: `${b.delay}s` }}
          title={b.name}
          data-testid={`brand-bubble-${b.key}`}
        >
          <img
            src={b.logo.src}
            alt={b.name}
            className="max-w-[70%] max-h-[70%] object-contain"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
          {hovered === b.key && <BrandCard b={b} side={b.cardSide} />}
        </div>
      ))}
    </div>
  );
};

export default BrandBubbles;
